import React, { useCallback, useState, useRef } from 'react';
import * as R from 'ramda';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';

import Icon from '@mui/material/Icon';
import Button from '@mui/material/Button';

import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableSortLabel from '@mui/material/TableSortLabel';
import TableRow from '@mui/material/TableRow';

import { returnField } from '../../utils/formHelpers';
import { ascComparator, descComparator } from '../../utils/comparators';
import { Field } from '../Field/Field.js'

const inlineStyles = {
  rightIcon: {
    marginLeft: 1,
  },
  tableLabel: {
    cursor: 'pointer',
    backgroundColor: 'rgb(227, 238, 255)',
    borderBottom: '2px solid black',
    height: '60px',
  },
};

const MailToUsers = ({
  doc,
  filteredAttachedSpaces,
  initialValues,
  projectUsers,
  t,
  users
}) => {
  const [formValues, setFormValues] = useState(initialValues)
  const [sortOrder, setSortOrder] = useState('asc');
  const [orderByStr, setOrderByStr] = useState('company');
  const copyAreaRef = useRef(null);

  const allSelected = Object.values(formValues).every(value => value)

  const toggleSelectAll = useCallback(() => {
    setFormValues(
      Object.keys(formValues).reduce((acc, currentKey) => {
        acc[currentKey] = !allSelected
        return acc
      }, {})
    )
  }, [
    allSelected,
    formValues
  ])

  const sendEmail = useCallback(obj => {
    const sendTo = R.reject(n => n === false, obj);
    const emails = R.values(R.pick(R.keys(sendTo), users)).map(
      user => user.profile.email
    ).join('; ')

    copyContents(emails);

    if (window) {
      window.location.href = `mailto:?bcc=${emails}`;
    }
  }, [users]);

  const copyContents = emails => {
    navigator.clipboard.writeText(emails).catch(err => {
      console.error('Failed to copy emails: ', err);
    });
  };

  const changeSortOrder = () => {
    setSortOrder(prevOrder => (prevOrder === 'asc' ? 'desc' : 'asc'));
  };

  const changeSortBy = sortByThis => {
    if (sortByThis === orderByStr) {
      changeSortOrder();
    } else {
      setOrderByStr(sortByThis);
    }
  };

  const enhanceUserArray = usersArray => {
    return usersArray.map(user => {
      const uid = R.path(['profile', 'uid'], user);
      const isOwnedByThisUser = val =>
        R.path(['users', uid], val) !== undefined;
      const filteredRelatedToThisUserAttachedSpaces = R.pickBy(
        isOwnedByThisUser,
        filteredAttachedSpaces
      );
      const isActiveAndCandidate = val =>
        R.path(['active'], val) && R.path(['candidate'], val);
      const filteredRelatedToThisUserActiveAndCandidateAttachedSpaces = R.pickBy(
        isActiveAndCandidate,
        filteredRelatedToThisUserAttachedSpaces
      );

      return {
        ...user,
        company: R.path(['profile', 'company'], user),
        participationStatus: projectUsers[uid],
        spacesAdded: `${
          R.keys(filteredRelatedToThisUserActiveAndCandidateAttachedSpaces)
            .length
        }/${R.keys(filteredRelatedToThisUserAttachedSpaces).length}`,
      };
    });
  };

  const rows = R.sort(
    sortOrder === 'asc'
      ? ascComparator(orderByStr)
      : descComparator(orderByStr),
    enhanceUserArray(R.values(users))
  );
  const theads = [
    'participationStatus',
    'spacesAdded',
    'name',
    'company',
    'role',
    'area',
    'email',
    'phone',
  ];

  const handleSubmit = useCallback(event => {
    event.preventDefault()
    sendEmail(formValues)
  }, [
    formValues,
    sendEmail
  ])

  const tableBodyMaxHeight = window.innerHeight * 0.5;
  return (
    <div>
      <form onSubmit={handleSubmit}>
        <div
          style={{
            maxHeight: `${tableBodyMaxHeight}px`,
            overflowY: 'auto',
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell
                  style={{
                    padding: '15px',
                    backgroundColor: 'rgb(227, 238, 255)',
                    borderBottom: '2px solid black',
                  }}
                >
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={toggleSelectAll}
                  >
                    {!allSelected
                      ? t('participants.selectAll')
                      : t('participants.clearSelection')}
                  </Button>
                </TableCell>
                {theads &&
                  theads.map(item => (
                    <TableCell
                      key={item}
                      sx={inlineStyles.tableLabel}
                      onClick={() => changeSortBy(item)}
                    >
                      <TableSortLabel
                        active={orderByStr === item}
                        direction={sortOrder}
                        sx={inlineStyles.sortLabel}
                      >
                        {t(`participants.${item}`)}
                      </TableSortLabel>
                    </TableCell>
                  ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(user => {
                const uid = user.profile.uid;
                return (
                  <Field
                    component={returnField('CheckBoxListUsers')}
                    filteredAttachedActiveAndCandidateSpaces={
                      enhanceUserArray([user])[0]
                        .filteredRelatedToThisUserActiveAndCandidateAttachedSpaces
                    }
                    filteredAttachedSpaces={
                      enhanceUserArray([user])[0]
                        .filteredRelatedToThisUserAttachedSpaces
                    }
                    id={uid}
                    item={user}
                    key={uid}
                    name={uid}
                    pid={doc}
                    projectUsers={projectUsers}
                    setValue={setFormValues}
                    t={t}
                    type="text"
                    uid={uid}
                    value={formValues[uid] || false}
                  />
                );
              })}
            </TableBody>
          </Table>
        </div>
        <br />
        <Button type="submit" variant="contained" color="primary">
          {t('mail.sendEmail')}
          <Icon sx={inlineStyles.rightIcon}>email</Icon>
        </Button>
      </form>
      <br />
      <div id="copyArea" contentEditable="true" ref={copyAreaRef} />
    </div>
  );
};

const mapStateToProps = (state, ownProps) => {
  const attachedSpaces = R.path(['space', 'attachedSpaces'], state);
  const pid = R.path(['doc'], ownProps);
  const isPartOfProject = (val) => val.project === pid;
  const filteredAttachedSpaces = R.pickBy(isPartOfProject, attachedSpaces);

  const projectUsers = R.path(['project', 'projects', pid, 'users'], state);
  const projectStatus = R.path(
    ['project', 'projects', pid, 'projectInfo', 'status'],
    state
  );

  const users = R.map(() => false, R.path(['users'], ownProps));

  return {
    projectStatus,
    projectUsers,
    filteredAttachedSpaces,
    initialValues: users,
  };
};

export default withTranslation('translations')(
  connect(mapStateToProps, null)(MailToUsers)
);
